import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import './settings.css';

const API_URL = import.meta.env.VITE_API_URL;

/* ===============================================================
   メール設定（3種）初期値
   =============================================================== */
const MAIL_TYPES = [
  { key: 'invite', label: '招待用', purpose: 'invite' },
  { key: 'support', label: 'サポート', purpose: 'support' },
  { key: 'notice', label: 'お知らせ', purpose: 'notice' },
];

const INITIAL_MAIL_SENDER = {
  email: '',
  display_name: '',
  reply_to: '',
  bcc: '',
  headers: '',   // ← textarea 用の1行形式で使用
  host: '',
  port: 587,
  secure: true,
  user: '',
  password: '',
};

const INITIAL_MAIL = MAIL_TYPES.reduce((acc, t) => {
  acc[t.key] = { ...INITIAL_MAIL_SENDER };
  return acc;
}, {});

/* ===============================================================
   Main Component
   =============================================================== */
const MailSettings = () => {
  const [mail, setMail] = useState(INITIAL_MAIL);
  const [activeMailType, setActiveMailType] = useState('invite');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const ensureCsrf = async () => {
    await axios.get(`${API_URL}/api/session/`);
    return Cookies.get('csrftoken');
  };

  /** 取得 */
  const fetchSettings = async () => {
    setErr('');
    setMsg('');
    setLoading(true);

    try {
      const r = await axios.get(`${API_URL}/api/system/settings/`);

      if (r.data?.success && r.data.mail) {
        const nextMail = { ...INITIAL_MAIL };

        MAIL_TYPES.forEach(t => {
          const src = r.data.mail[t.key];
          if (src) {
            // JSON -> string 変換 + {}除去
            const obj = src.headers || {};
            const jsonStr = JSON.stringify(obj, null, 2);
            const trimmed = jsonStr.replace(/^{\s*|\s*}$/g, '').trim(); // ← {}を削除

            nextMail[t.key] = {
              ...nextMail[t.key],
              ...src,
              headers: trimmed,
              password: "",
            };
          }
        });

        setMail(nextMail);
      } else {
        setErr('設定の取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      setErr('設定の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  /** 保存 */
  const saveAll = async () => {
    setErr('');
    setMsg('');
    setSaving(true);

    try {
      const token = await ensureCsrf();

      // textareaの行を JSON に戻す
      const cleanMail = JSON.parse(JSON.stringify(mail));
      MAIL_TYPES.forEach(t => {
        const raw = cleanMail[t.key].headers?.trim();
        if (!raw) {
          cleanMail[t.key].headers = {};
          return;
        }
        try {
          // {} を付与して JSON.parse
          cleanMail[t.key].headers = JSON.parse(`{${raw}}`);
        } catch (e) {
          cleanMail[t.key].headers = {};
        }
      });

      await axios.put(
        `${API_URL}/api/system/settings_update/`,
        { mail: cleanMail }
      );

      setMsg('保存しました');
    } catch (e) {
      console.error(e);
      setErr('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const current = mail[activeMailType];

  const handleChange = (field, value) => {
    setMail(prev => ({
      ...prev,
      [activeMailType]: { ...prev[activeMailType], [field]: value },
    }));
  };

  return (
    <div>
      <h2 className="pageTitle">送信元メール設定</h2>

      {err && <div className="error-alert">{err}</div>}
      {msg && <div className="success-alert">{msg}</div>}

      <div className="mailTabs">
        {MAIL_TYPES.map(t => (
          <button
            key={t.key}
            className={
              t.key === activeMailType
                ? 'mailTabButton mailTabButtonActive'
                : 'mailTabButton'
            }
            onClick={() => setActiveMailType(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="detailCard">
        <div className="detailHead">
          <div className="listTitle">{MAIL_TYPES.find(t => t.key === activeMailType)?.label}</div>
          <div className="listOps">
            <button className="btn primary" onClick={saveAll} disabled={saving}>
              {saving ? '保存中…' : '保存'}
            </button>
          </div>
        </div>

        <div className="formGrid">
          <label>
            送信元メールアドレス
            <input type="email" value={current.email}
              onChange={e => handleChange('email', e.target.value)} />
          </label>

          <label>
            表示名
            <input type="text" value={current.display_name}
              onChange={e => handleChange('display_name', e.target.value)} />
          </label>

          <label>
            Reply-To
            <input type="email" value={current.reply_to}
              onChange={e => handleChange('reply_to', e.target.value)} />
          </label>

          <label>
            BCC
            <input type="text" value={current.bcc}
              onChange={e => handleChange('bcc', e.target.value)} />
          </label>

          <label>
            SMTPホスト
            <input type="text" value={current.host}
              onChange={e => handleChange('host', e.target.value)} />
          </label>

          <label>
            ポート
            <input type="number" value={current.port}
              onChange={e => handleChange('port', Number(e.target.value))} />
          </label>

          <label>
            ユーザー
            <input type="text" value={current.user}
              onChange={e => handleChange('user', e.target.value)} />
          </label>

          <label>
            パスワード
            <input type="password" value={current.password}
              onChange={e => handleChange('password', e.target.value)}
              placeholder="変更しない場合は空のまま" />
          </label>

          <label>
            TLS/SSL
            <div className="switchRow">
              <input
                id={`secure_${activeMailType}`}
                type="checkbox"
                checked={current.secure}
                onChange={e => handleChange('secure', e.target.checked)}
              />
              <label htmlFor={`secure_${activeMailType}`} className="inline">有効</label>
            </div>
          </label>

          <label>
            追加ヘッダ
            <textarea
              value={current.headers}
              onChange={e => handleChange('headers', e.target.value)}
              placeholder={`"List-Unsubscribe": "<https://example.com/unsubscribe>"`}
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default MailSettings;
